import { RouterProvider } from 'react-router-dom';
import { QueryClient, QueryClientProvider } from '@tanstack/react-query';
import { ReactQueryDevtools } from '@tanstack/react-query-devtools';
import * as Sentry from '@sentry/react';
import RouterWrapper from './router';
import Toaster from './components/ui/toaster';
import { EnvironmentTypes } from '@common/constants';

const queryClient = new QueryClient({
  defaultOptions: {
    queries: {
      staleTime: 30_000, // 30 seconds
      retry: 1,
      refetchOnWindowFocus: false,
    },
    mutations: {
      retry: 0,
    },
  },
});

Sentry.init({
  dsn: import.meta.env.VITE_SENTRY_DSN,
  enabled: import.meta.env.MODE !== EnvironmentTypes.DEVELOPMENT,
  environment: import.meta.env.VITE_SENTRY_ENVIRONMENT || EnvironmentTypes.PRODUCTION,
  integrations: [
    Sentry.browserTracingIntegration(),
    Sentry.replayIntegration({
      maskAllText: false,
      blockAllMedia: false,
    }),
  ],
  // Performance Monitoring: 10% sample rate in production, 100% in development
  tracesSampleRate: import.meta.env.MODE === EnvironmentTypes.DEVELOPMENT ? 1.0 : 0.1,
  // Only trace requests to our own API
  tracePropagationTargets: [import.meta.env.VITE_API_BASE_URL || 'localhost'],
  // Session Replay
  replaysSessionSampleRate: 0.1, // This sets the sample rate at 10%. You may want to change it to 100% while in development and then sample at a lower rate in production.
  replaysOnErrorSampleRate: 1.0, // If you're not already sampling the entire session, change the sample rate to 100% when sampling sessions where errors occur.

  // Better error grouping for similar issues
  attachStacktrace: true,

  beforeSend(event) {
    // Add release information when available in env
    if (import.meta.env.VITE_APP_VERSION) {
      event.release = import.meta.env.VITE_APP_VERSION;
    }

    return event;
  },

  // Custom error tags
  initialScope: {
    tags: {
      app: 'super-admin',
      environment: import.meta.env.MODE || 'production',
    },
  },
});

function Root() {
  const isDevelopment = import.meta.env.MODE === EnvironmentTypes.DEVELOPMENT;

  return (
    <QueryClientProvider client={queryClient}>
      <Toaster />
      <RouterProvider router={RouterWrapper()} />
      {isDevelopment && <ReactQueryDevtools initialIsOpen={false} />}
    </QueryClientProvider>
  );
}

export default Root;
